import { defineStore, storeToRefs } from 'pinia';
import { ref, watch } from 'vue';
import {
  ChatState,
  Message,
  type AlarmRecommendation,
  type AlarmRecommendMetric,
} from '@/ai/utils/types';
import { customAxiosInstance } from '@/common/api/utils/mutator';
import { useMyInfoStore } from '@/config/stores/my-info';
import { i18n } from '@/common/locale';
import {
  streamChatbotMessage,
  type AlarmContext,
  type SelectedTarget,
} from '@/ai/utils/streamChatbot';
import { useChatConversationsStore } from '@/ai/store/chatConversations';
import {
  collectPageContext,
  registerChatbotScreenPrefill,
  serializePageContext,
} from '@/ai/utils/pageContext';
import router from '@/common/router';
import { useSlideDetailStore, TOTAL_DETAIL } from '@/common/stores/slide-detail';
// AIOps 슬라이드(인사이트/사용자 알람) addition 빌더 — backend raw SummaryItem 을 슬라이드 addition 으로 매핑.
// chat store 는 ai/ 도메인이지만 aiops 슬라이드 빌더 재사용을 위해 cross-domain import.
import {
  buildEventAlertAdditionFromAlertRule,
  buildInsightAdditionFromInsight,
} from '@/aiops/views/dashboard/aiOpsDashboardView.utils';
import type { AlertRuleSummaryItem, InsightSummaryItem } from '@/api-client/aiops/model';
import {
  DEFAULT_CHAT_STATE,
  MIN_WIDTH,
  MIN_HEIGHT,
  BUTTON_SIZE,
  SCREEN_PADDING,
  WINDOW_SIZE_RATIO,
  STOPPED_BY_USER_TEXT,
} from '@/ai/utils/defined';

/**
 * 챗봇이 open_target_detail 도구로 매칭한 detail block 의 형태.
 * 백엔드 OpenTargetDetailTool 의 detail map 과 1:1 매핑.
 */
/**
 * NavigateDetailInfo.type 의 허용 값. stream payload 의 wide `string` 을 runtime 으로
 * 좁히기 위한 가드. union 멤버 추가 시 본 배열도 동기 갱신 필요.
 */
const ALLOWED_DETAIL_TYPES = [
  'host',
  'was',
  'web_server',
  'instance',
  'pod',
  'node',
  'namespace',
  'cluster',
  'container',
  'network_device',
  'alert_rule',
  'insight',
  'event_alert',
  'dashboard',
] as const;

interface NavigateDetailInfo {
  type: (typeof ALLOWED_DETAIL_TYPES)[number];
  target_id: string;
  target_name?: string;
  /** sub_kind: was 의 'java/dotnet/python/nodejs' · database 의 'oracle/postgresql/mysql' 등 */
  sub_kind?: string;
  /** host 인 경우 'host' or 'container_host' */
  server?: string;
  /** alert_rule 전용 — backend 의 OpenAlertRuleDetailTool 이 보내는 메타. */
  alert_rule_id?: string;
  alert_rule_name?: string;
  alert_rule_type?: string;
  /** insight / event_alert 전용 — backend OpenAiOpsSlideTool 이 SummaryItem 직렬화 그대로 첨부. */
  raw?: unknown;
  /**
   * Detail slide-out 의 초기 tab 식별자. 사용자가 "락 분석", "active 트랜잭션",
   * "메트릭 추이" 등 명시적인 분석 의도를 보일 때 backend OpenTargetDetailTool 이
   * 채워 보내거나 markdown 링크의 `?tab=<value>` 로 전달. detail 의
   * selectedTabComputed 가 자동으로 state.selectedTab 으로 사용.
   * 형식: ASCII camelCase (^[a-z][a-zA-Z0-9]{1,30}$).
   */
  tab?: string;
}

/**
 * detail.type 별로 slide-detail 의 addTabPanel payload 모양이 달라 조건 분기.
 *   - host          : { type: HOST, host, name, addition.server }
 *   - was           : { type: WAS, was: { wasId, type:sub_kind }, name }
 *   - web_server    : { type: WEB_SERVER, webServer: { wasId, type:sub_kind }, name }
 *   - instance      : { type: INSTANCE, instance: { instanceId, dbType:sub_kind }, name }
 *   - pod           : { type: POD, addition: { uid:target_id }, name }
 *   - node          : { type: NODE, addition: { uid:target_id }, name }
 *   - namespace     : { type: NAMESPACE, addition: { uid:target_id }, name }
 *   - container     : { type: K8S_CONTAINER or CONTAINER_HOST(server hint), container:target_id, addition }
 *   - network_device: { type: NETWORK_DEVICE, addition: { deviceId:target_id, networkDeviceName } }
 *   - cluster (kuber) : 라우팅만 수행 (페이지 자체가 detail role) — backend 가 detail 미발사.
 */
const openSlideDetailFromChatbot = async (detail: NavigateDetailInfo) => {
  const slideStore = useSlideDetailStore();
  const name = detail.target_name ?? detail.target_id;
  // detail.tab 이 있으면 slide-detail 의 selectedTabComputed 가 자동으로 초기 tab 으로 사용.
  // detail 컴포넌트의 correctSelectedTab/watcher 가 invalid tab 이면 default 로 graceful fallback.
  const stateForTab = detail.tab ? ({ selectedTab: detail.tab } as never) : undefined;
  console.info('[chatbot] openSlideDetailFromChatbot', {
    type: detail.type,
    target_id: detail.target_id,
    sub_kind: detail.sub_kind,
    tab: detail.tab,
    stateForTab,
  });
  switch (detail.type) {
    case 'host':
      slideStore.addTabPanel({
        type: TOTAL_DETAIL.HOST,
        host: detail.target_id,
        name,
        addition: { server: detail.server ?? 'host' },
        state: stateForTab,
      });
      return;
    case 'was':
      slideStore.addTabPanel({
        type: TOTAL_DETAIL.WAS,
        name,
        was: {
          wasId: detail.target_id,
          type: (detail.sub_kind || '') as never,
        },
        state: stateForTab,
      });
      return;
    case 'web_server':
      slideStore.addTabPanel({
        type: TOTAL_DETAIL.WEB_SERVER,
        name,
        webServer: {
          wasId: detail.target_id,
          type: (detail.sub_kind || '') as never,
        } as never,
        state: stateForTab,
      });
      return;
    case 'instance':
      slideStore.addTabPanel({
        type: TOTAL_DETAIL.INSTANCE,
        name,
        instance: {
          instanceId: detail.target_id,
          dbType: (detail.sub_kind || '') as never,
        },
        state: stateForTab,
      });
      return;
    case 'pod':
      slideStore.addTabPanel({
        type: TOTAL_DETAIL.POD,
        name,
        addition: { uid: detail.target_id } as never,
        state: stateForTab,
      });
      return;
    case 'node':
      slideStore.addTabPanel({
        type: TOTAL_DETAIL.NODE,
        name,
        addition: { uid: detail.target_id } as never,
        state: stateForTab,
      });
      return;
    case 'namespace':
      slideStore.addTabPanel({
        type: TOTAL_DETAIL.NAMESPACE,
        name,
        addition: { uid: detail.target_id } as never,
        state: stateForTab,
      });
      return;
    case 'container': {
      // sub_kind 로 K8s vs Container Host 구분. 둘 다 컨테이너지만 panel 분기.
      const isContainerHost = (detail.sub_kind || '').toLowerCase() === 'container_host';
      if (isContainerHost) {
        slideStore.addTabPanel({
          type: TOTAL_DETAIL.CONTAINER_HOST,
          name,
          container: detail.target_id,
          addition: { server: 'container_host', containerHostId: detail.target_id } as never,
          state: stateForTab,
        });
      } else {
        slideStore.addTabPanel({
          type: TOTAL_DETAIL.K8S_CONTAINER,
          name,
          container: detail.target_id,
          addition: { server: 'k8s' } as never,
          state: stateForTab,
        });
      }
      return;
    }
    case 'network_device':
      slideStore.addTabPanel({
        type: TOTAL_DETAIL.NETWORK_DEVICE,
        name,
        addition: {
          deviceId: detail.target_id,
          networkDeviceName: name,
        } as never,
        state: stateForTab,
      });
      return;
    case 'alert_rule': {
      // alertRuleDetail.adapter 는 useMessage()/useInternational() (inject) 를 호출하여
      // setup() 컨텍스트 밖(=chat store action)에서 실행 시 inject 가 undefined 라 silently 실패.
      // → 어댑터를 거치지 않고 slideStore.addTabPanel 을 직접 호출 (다른 host/was 케이스와 동일 패턴).
      const ruleId = detail.alert_rule_id ?? detail.target_id;
      const ruleName = detail.alert_rule_name ?? detail.target_name ?? '';
      const ruleType = detail.alert_rule_type ?? '';
      slideStore.addTabPanel({
        type: TOTAL_DETAIL.ALERT,
        name: ruleName || ruleId,
        addition: {
          alertRuleId: ruleId,
          alertRuleName: ruleName,
          alertRuleType: ruleType,
        } as never,
        state: stateForTab,
      });
      return;
    }
    case 'insight': {
      // AIOps 대시보드의 AI 인사이트 / AI 이상탐지 슬라이드.
      // detail.raw 는 backend InsightSummaryItem 직렬화 그대로. 빌더가 있으면 사용,
      // 없으면 최소 addition(rcaId 만) 으로 fallback.
      const raw = (detail.raw ?? null) as InsightSummaryItem | null;
      slideStore.addTabPanel({
        title: detail.target_name,
        name: detail.target_name || detail.target_id || '',
        type: TOTAL_DETAIL.INSIGHT,
        init: true,
        addition: (raw
          ? buildInsightAdditionFromInsight(raw)
          : {
              resultId: '',
              ruleId: '',
              ruleName: detail.target_name ?? '',
              rcaId: detail.target_id ?? '',
            }) as never,
        state: stateForTab,
      });
      return;
    }
    case 'event_alert': {
      // AIOps 대시보드의 사용자 알람 슬라이드. backend raw 가 AlertRuleSummaryItem 직렬화.
      const raw = (detail.raw ?? null) as AlertRuleSummaryItem | null;
      if (!raw) {
        console.warn('[chatbot] event_alert detail missing raw, cannot open slide');
        return;
      }
      slideStore.addTabPanel({
        name: detail.target_name ?? raw.alertRuleName ?? '',
        type: TOTAL_DETAIL.EVENT_ALERT,
        init: true,
        addition: buildEventAlertAdditionFromAlertRule(raw),
        state: stateForTab,
      });
      return;
    }
    default:
      // 미매핑 타입 — 라우팅만 수행되고 사용자가 직접 행을 클릭.
      console.info(
        '[chatbot] open_target_detail: routed to list, but auto-detail open not wired for type:',
        detail.type,
      );
  }
};

const getDefaultChatPosition = (): ChatState => {
  const storedState = localStorage.getItem('chatState');
  return storedState ? JSON.parse(storedState) : DEFAULT_CHAT_STATE;
};

const adjustWindowSize = (state: ChatState, innerWidth: number, innerHeight: number): ChatState => {
  const adjustedState = { ...state };

  if (adjustedState.windowSize.width > innerWidth) {
    adjustedState.windowSize.width = Math.max(innerWidth * WINDOW_SIZE_RATIO, MIN_WIDTH);
  }
  if (adjustedState.windowSize.height > innerHeight) {
    adjustedState.windowSize.height = Math.max(innerHeight * WINDOW_SIZE_RATIO, MIN_HEIGHT);
  }

  return adjustedState;
};

const adjustWindowPosition = (
  state: ChatState,
  innerWidth: number,
  innerHeight: number,
): ChatState => {
  const adjustedState = { ...state };

  if (adjustedState.window.left + adjustedState.windowSize.width > innerWidth) {
    adjustedState.window.left = innerWidth - adjustedState.windowSize.width;
  }
  if (adjustedState.window.top + adjustedState.windowSize.height > innerHeight) {
    adjustedState.window.top = innerHeight - adjustedState.windowSize.height;
  }

  adjustedState.window.left = Math.max(0, adjustedState.window.left);
  adjustedState.window.top = Math.max(0, adjustedState.window.top);

  return adjustedState;
};

const adjustButtonPosition = (
  state: ChatState,
  innerWidth: number,
  innerHeight: number,
): ChatState => {
  const adjustedState = { ...state };

  if (adjustedState.button.left + BUTTON_SIZE > innerWidth) {
    adjustedState.button.left = innerWidth - BUTTON_SIZE - SCREEN_PADDING;
  }
  if (adjustedState.button.top + BUTTON_SIZE > innerHeight) {
    adjustedState.button.top = innerHeight - BUTTON_SIZE - SCREEN_PADDING;
  }

  adjustedState.button.left = Math.max(SCREEN_PADDING, adjustedState.button.left);
  adjustedState.button.top = Math.max(SCREEN_PADDING, adjustedState.button.top);

  return adjustedState;
};

const adjustPositionToScreen = (state: ChatState): ChatState => {
  const { innerWidth, innerHeight } = window;
  let adjustedState = { ...state };

  adjustedState = adjustWindowSize(adjustedState, innerWidth, innerHeight);
  adjustedState = adjustWindowPosition(adjustedState, innerWidth, innerHeight);
  adjustedState = adjustButtonPosition(adjustedState, innerWidth, innerHeight);

  return adjustedState;
};

export type DockMode = 'docked' | 'floating';
const DOCK_MODE_KEY = 'chatDockMode';
const DOCK_WIDTH_KEY = 'chatDockWidth';
const DEFAULT_DOCK_WIDTH = 420;
const MIN_DOCK_WIDTH = 320;
const MAX_DOCK_WIDTH = 720;

const getStoredDockMode = (): DockMode => {
  const v = localStorage.getItem(DOCK_MODE_KEY);
  return v === 'floating' ? 'floating' : 'docked';
};
const getStoredDockWidth = (): number => {
  const raw = Number(localStorage.getItem(DOCK_WIDTH_KEY) || NaN);
  if (Number.isFinite(raw)) {
    return Math.min(MAX_DOCK_WIDTH, Math.max(MIN_DOCK_WIDTH, Math.round(raw)));
  }
  return DEFAULT_DOCK_WIDTH;
};

export const useChatStore = defineStore('chat', () => {
  const { myInfo: userInfo } = storeToRefs(useMyInfoStore());
  const chatPosition = ref<ChatState>(DEFAULT_CHAT_STATE);
  const chatMessages = ref<Message[]>([]);
  let messageIdSeq = 0; // 메시지 안정 id 시퀀스 (addMessage 에서 증가)
  const isChatWindowOpen = ref<boolean>(false);
  const isLoading = ref<boolean>(false);

  /** 'docked': 우측 sidebar (사용자 요청 디폴트). 'floating': 기존 자유 위치/크기. */
  const dockMode = ref<DockMode>(getStoredDockMode());
  /** docked 일 때의 가로 폭 (px). MIN/MAX 범위 보정. */
  const dockWidth = ref<number>(getStoredDockWidth());

  const setDockMode = (mode: DockMode) => {
    dockMode.value = mode;
    localStorage.setItem(DOCK_MODE_KEY, mode);
  };
  const toggleDockMode = () => {
    setDockMode(dockMode.value === 'docked' ? 'floating' : 'docked');
  };
  const setDockWidth = (px: number) => {
    const clamped = Math.min(MAX_DOCK_WIDTH, Math.max(MIN_DOCK_WIDTH, Math.round(px)));
    dockWidth.value = clamped;
    localStorage.setItem(DOCK_WIDTH_KEY, String(clamped));
  };

  // ──────────────────────────────────────────────────────────────────
  // 타겟 선택 ('+' 버튼) — feature: chatbot-target-conversation
  //   selectedTargets : 현재 발화에 동봉할 선택 타겟 (칩 표시 + stream targets)
  //   pinnedTargetIds : 즐겨찾기 고정 (레이어 상단 '핀' 섹션)
  //   recentTargetIds : 최근 대화/선택 (레이어 상단 '최근' 섹션, 최신 상위)
  // 셋 다 사용자별 localStorage 영속 (다음 대화에서도 유지/추천). raw localStorage — 챗봇 컨벤션.
  // ──────────────────────────────────────────────────────────────────
  /** 한 발화에 동봉 가능한 선택 타겟 상한. */
  const MAX_SELECTED_TARGETS = 20;
  /** 최근 추천 타겟 보관 상한. */
  const MAX_RECENT_TARGETS = 10;

  const selectedTargets = ref<SelectedTarget[]>([]);
  const pinnedTargetIds = ref<string[]>([]);
  const recentTargetIds = ref<string[]>([]);
  /** 사용자별 키 — userInfo 가 늦게 로드되므로 접근 시점에 평가. */
  const targetKey = (base: string): string => `${base}:${userInfo.value?.userId ?? 'anon'}`;

  const readJson = <T>(key: string, fallback: T): T => {
    try {
      const raw = localStorage.getItem(key);
      return raw ? (JSON.parse(raw) as T) : fallback;
    } catch {
      return fallback;
    }
  };
  const writeJson = (key: string, value: unknown): void => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      /* ignore quota/serialization errors */
    }
  };

  /** 현재 userId 기준 영속값 로드 (reload/재로그인 복원). */
  const loadPersistedTargets = (): void => {
    selectedTargets.value = readJson<SelectedTarget[]>(targetKey('chatbotSelectedTargets'), []);
    pinnedTargetIds.value = readJson<string[]>(targetKey('chatbotPinnedTargets'), []);
    recentTargetIds.value = readJson<string[]>(targetKey('chatbotRecentTargets'), []);
  };
  // userId 가 valid 해지는 즉시 1회 복원 (immediate). 이후 사용자 전환 시에도 재로드.
  watch(
    () => userInfo.value?.userId,
    (uid) => {
      if (uid !== undefined && uid !== null && uid !== -1) loadPersistedTargets();
    },
    { immediate: true },
  );

  const isTargetSelected = (targetId: string): boolean =>
    selectedTargets.value.some((t) => t.targetId === targetId);

  /** 선택 추가 — 상한 초과면 false (호출측이 안내). 이미 있으면 true(무변경). */
  const addSelectedTarget = (target: SelectedTarget): boolean => {
    if (isTargetSelected(target.targetId)) return true;
    if (selectedTargets.value.length >= MAX_SELECTED_TARGETS) return false;
    selectedTargets.value = [...selectedTargets.value, target];
    writeJson(targetKey('chatbotSelectedTargets'), selectedTargets.value);
    return true;
  };
  const removeSelectedTarget = (targetId: string): void => {
    selectedTargets.value = selectedTargets.value.filter((t) => t.targetId !== targetId);
    writeJson(targetKey('chatbotSelectedTargets'), selectedTargets.value);
  };
  /** 토글 — 선택 추가/해제. 추가 시 상한 초과면 false. */
  const toggleSelectTarget = (target: SelectedTarget): boolean => {
    if (isTargetSelected(target.targetId)) {
      removeSelectedTarget(target.targetId);
      return true;
    }
    return addSelectedTarget(target);
  };
  const clearSelectedTargets = (): void => {
    selectedTargets.value = [];
    writeJson(targetKey('chatbotSelectedTargets'), selectedTargets.value);
  };

  const isTargetPinned = (targetId: string): boolean => pinnedTargetIds.value.includes(targetId);
  const togglePinTarget = (targetId: string): void => {
    pinnedTargetIds.value = isTargetPinned(targetId)
      ? pinnedTargetIds.value.filter((id) => id !== targetId)
      : [targetId, ...pinnedTargetIds.value];
    writeJson(targetKey('chatbotPinnedTargets'), pinnedTargetIds.value);
  };

  /** 최근 목록 갱신 — 주어진 id 들을 최신 상위로 prepend, dedupe, cap. */
  const pushRecentTargets = (targetIds: string[]): void => {
    if (!targetIds.length) return;
    const merged = [...targetIds, ...recentTargetIds.value];
    const deduped: string[] = [];
    for (const id of merged) {
      if (id && !deduped.includes(id)) deduped.push(id);
      if (deduped.length >= MAX_RECENT_TARGETS) break;
    }
    recentTargetIds.value = deduped;
    writeJson(targetKey('chatbotRecentTargets'), recentTargetIds.value);
  };

  const setChatPosition = (newPosition: Partial<ChatState>) => {
    const updatedPosition = {
      ...chatPosition.value,
      ...newPosition,
    };
    chatPosition.value = adjustPositionToScreen(updatedPosition);
    localStorage.setItem('chatState', JSON.stringify(chatPosition.value));
  };

  const openChat = () => {
    isChatWindowOpen.value = true;
  };

  const closeChat = () => {
    isChatWindowOpen.value = false;
  };

  const toggleChat = () => {
    isChatWindowOpen.value = !isChatWindowOpen.value;
  };

  /** 스토어 보관 메시지 상한 — 긴 세션에서 리포트/차트 JSON 을 포함한 메시지가 Pinia 배열에
   *  무한 누적되는 것 방지 (리뷰 지적 — 브라우저 메모리). 리포트 상세(findReportInMessages)도
   *  이 배열을 읽으므로 상한 내 최근 리포트만 세션 중 열람 가능 — 서버 이력은 그대로다. */
  const MAX_STORE_MESSAGES = 300;

  const addMessage = (messages: Message[], options?: { prepend?: boolean }) => {
    // 안정 고유 id 부여 (없을 때만) — v-for :key 가 슬라이스 idx 가 아닌 메시지 정체성을 따르게.
    // createdAt 은 여기서 찍지 않는다 — history(getChatMessage)도 이 경로를 타므로, 백엔드가 값을 안
    // 내려주면(구버전/과거 레코드) 전부 로드시각으로 오기록된다. 신규(전송/스트리밍) 메시지는
    // 전송 시점에서 직접 부여하고, history 에 값이 없으면 빈 채로 둔다(잘못된 시각보다 미표시가 안전).
    for (const m of messages) {
      if (m.id === undefined) m.id = `msg-${(messageIdSeq += 1)}`;
    }
    if (options?.prepend) {
      chatMessages.value.unshift(...messages);
    } else {
      chatMessages.value.push(...messages);
    }
    if (chatMessages.value.length > MAX_STORE_MESSAGES) {
      chatMessages.value.splice(0, chatMessages.value.length - MAX_STORE_MESSAGES);
    }
  };

  const clearMessages = () => {
    chatMessages.value = [];
  };

  const startLoading = () => {
    isLoading.value = true;
  };

  const stopLoading = () => {
    isLoading.value = false;
  };

  /** 이력 요청 sequence — 마지막 요청만 반영. 동시 중복 요청(onMounted+immediate watch)의
   *  이중 append 와 A→B→A 전환에서 뒤늦게 도착한 옛 응답의 재적용을 모두 차단 (리뷰 지적 —
   *  requestedConversationId 비교만으론 같은 대화로 돌아온 stale 응답을 구분 못 함). */
  let historyRequestSeq = 0;

  const getChatMessage = async (lastTime?: string) => {
    // userInfo 가 아직 로드되지 않았으면 (-1) skip — chatWindow.setup 에서 watch 로 재시도
    const uid = userInfo.value?.userId;
    if (uid === undefined || uid === null || uid === -1) {
      return;
    }
    const seq = ++historyRequestSeq;
    try {
      // GET 은 신규 대화를 만들지 않음 — 활성 conversationId 가 이미 있을 때만 동봉.
      // 미명시 시 backend 가 가장 최근 active 대화 반환 (없으면 빈 list).
      const convStore = useChatConversationsStore();
      const conversationId = convStore.activeId;
      const requestedConversationId = conversationId; // 응답 적용 시 대화 전환 여부 검사용 캡처
      const params = {
        userId: String(uid),
        roomId: 0,
        ...(conversationId && { conversationId }),
        ...(lastTime && chatMessages.value.length > 0 && { lastTime }),
      };

      const response = await customAxiosInstance<{ data: Message[] }>({
        url: '/api/v9/chatbot/message',
        method: 'GET',
        params,
      });

      const data = response.data;
      // 최신 요청이 아니면 폐기 — 동시 중복 요청의 이중 append / stale 응답 재적용 방지.
      if (seq !== historyRequestSeq) {
        return;
      }
      // 요청 중 대화가 전환됐으면 폐기 — 늦게 도착한 이전 대화 응답이 현재 대화에 섞이는 것 방지 (리뷰 지적)
      if (convStore.activeId !== requestedConversationId) {
        return;
      }
      if (data?.data) {
        const messages = lastTime ? data.data.filter((msg) => msg.sender === 'chatbot') : data.data;
        // 전체 이력(lastTime 없음)은 빈 대화에서만 요청되는데, 응답이 늦는 사이 사용자가
        // 발화했다면 이력은 그 발화보다 과거 기록이다 — 뒤에 append 하면 순서가 깨지므로
        // 앞에 붙인다 (요청 시작 시점 스냅샷이라 현재 발화는 포함되지 않음, 리뷰 P1-4).
        addMessage(messages, { prepend: !lastTime && chatMessages.value.length > 0 });
      }
    } catch (e) {
      console.log(e);
    } finally {
      // 스트리밍 진행 중에는 끄지 않는다 — 공용 isLoading 의 소유자는 SSE 스트림이므로,
      // 병행한 이력 요청의 finally 가 스트리밍 로딩 표시를 꺼버리는 레이스 방지 (리뷰 지적).
      if (!streamAbortController) {
        stopLoading();
      }
    }
  };

  /** 현재 진행 중인 streaming 응답 (없으면 null). UI 의 BotMessage 가 isPartial=true 로 점진 렌더. */
  const partialBotMessage = ref<Message | null>(null);
  let streamAbortController: AbortController | null = null;

  /**
   * 직전 send 시점의 페이지 path. 다음 send 에서 pageContext.previousPath 로 동봉되어
   * 백엔드 system prompt 가 (current path != previous path) → 새 화면 진입을 인지하도록 한다.
   */
  let lastSentPath: string | undefined;

  /**
   * 사용자 메시지를 보내고 SSE 로 chatbot 응답을 점진 수신.
   * 1) chatMessages 에 user 메시지 push (options.displayText 가 있으면 그것을, 없으면 userText)
   * 2) chatMessages 에 partial bot 메시지 push (isPartial=true)
   * 3) tool_call/tool_result/token 이벤트로 partial 메시지 누적 갱신
   * 4) done 이벤트 시 partial 플래그 해제
   *
   * @param userText  Gemini 에 실제로 보내는 본문 (raw chart JSON 등 포함 가능)
   * @param options.displayText  채팅창에 표시할 짧은 사용자 메시지. 미지정 시 userText 가 그대로 표시됨.
   *                             AnalysisButton 처럼 raw 데이터를 함께 보내야 하지만 사용자에겐
   *                             간결한 메시지만 보이도록 분리하고 싶을 때 사용.
   */
  const sendStreamingMessage = async (
    userText: string,
    options?: {
      displayText?: string;
      /** AnalysisButton 등에서 캡쳐한 차트 이미지 dataURL — UserMessage 썸네일로 표시. */
      imageUrl?: string;
      /** 썸네일 클릭 시 라우팅할 path. */
      sourcePath?: string;
      /** 썸네일 caption. */
      sourceTitle?: string;
      /**
       * "보이지 않는" 추가 prompt — backend 가 마지막 user turn 에만 임시로 append 하고
       * history/화면에는 저장하지 않음. 차트 raw JSON 처럼 noisy 한 데이터를 전달할 때 사용.
       */
      hiddenContext?: string;
      /**
       * 선제 알람 RCA 진입 컨텍스트 — 알람 카드 클릭 시 동봉. backend 가 RCA 지시문으로 변환.
       * (feature: chatbot-proactive-alarm)
       */
      alarmContext?: AlarmContext;
      /**
       * partial bot 메시지가 token 으로 갱신될 때마다 호출되는 콜백.
       * AnalysisBalloon 등 외부 UI 가 같은 stream 을 동기화 표시할 때 사용.
       */
      onTokenAppend?: (fullText: string) => void;
      /** stream 종료(또는 에러) 시 호출. */
      onStreamDone?: (fullText: string, ok: boolean) => void;
    },
  ) => {
    if (!userText || !userText.trim()) return;
    // 이전 stream 이 진행 중이면 중단
    if (streamAbortController) {
      streamAbortController.abort();
      streamAbortController = null;
    }

    const userIdStr = String(userInfo.value?.userId ?? -1);
    const userMsg: Message = {
      sender: userIdStr,
      message: options?.displayText ?? userText,
      messageType: 'text',
      resultType: '',
      result: '',
      imageUrl: options?.imageUrl ?? '',
      sourcePath: options?.sourcePath,
      sourceTitle: options?.sourceTitle,
      createdAt: new Date().toISOString(), // 전송 시각
    };
    addMessage([userMsg]);

    addMessage([
      {
        sender: 'chatbot',
        message: '',
        messageType: 'text',
        resultType: '',
        result: '',
        imageUrl: '',
        isPartial: true,
        toolCalls: [],
        createdAt: new Date().toISOString(), // 시작 시각 — 완료 시 onDone 에서 갱신
      },
    ]);
    // chatMessages 에 push 되면서 reactive proxy 가 만들어진다 — 그 proxy 를 잡아서 mutate 해야 v-for 가 갱신됨.
    const partial = chatMessages.value[chatMessages.value.length - 1];
    partialBotMessage.value = partial;
    startLoading();

    // 사용자가 현재 보고 있는 화면 컨텍스트 캡처 — 챗봇이 "이 화면이 뭐예요?" 질문 등에 답하는 데 사용.
    // previousPath 를 첨부하여 backend 가 (current != previous) 시 새 화면 진입으로 disambiguate.
    const collected = collectPageContext();
    if (lastSentPath !== undefined && lastSentPath !== collected.path) {
      collected.previousPath = lastSentPath;
    }
    const pageContext = serializePageContext(collected);
    lastSentPath = collected.path;

    // 요청별 controller — 공유 변수에는 대입하되, 정리(finally)는 아래에서
    // '여전히 내 controller 인 경우에만' 수행 — 연속 전송으로 A를 취소하고 B가 시작된 뒤
    // A의 finally 가 B의 controller/로딩 상태를 지우는 레이스 방지 (리뷰 지적).
    const requestController = new AbortController();
    streamAbortController = requestController;
    // 대화 ID — 활성 대화가 있으면 그걸로 append, 없으면 backend 가 자동 새 대화 생성.
    const convStore = useChatConversationsStore();
    const conversationId = convStore.activeId ?? undefined;
    // 사용자가 '+' 버튼으로 지정한 타겟 — 비어있으면 미동봉(기존 동작 그대로).
    const targets = selectedTargets.value.length ? [...selectedTargets.value] : undefined;
    // 동봉한 타겟은 '최근' 추천에 반영 (다음 레이어 오픈 시 상단 추천).
    if (targets) pushRecentTargets(targets.map((tg) => tg.targetId));
    try {
      await streamChatbotMessage(
        {
          userId: userIdStr,
          conversationId,
          message: userText,
          pageContext,
          hiddenContext: options?.hiddenContext,
          alarmContext: options?.alarmContext,
          targets,
        },
        {
          onToolCall: ({ name, args }) => {
            if (!partial.toolCalls) partial.toolCalls = [];
            partial.toolCalls.push({ name, args, status: 'running' });
          },
          onToolResult: ({ name, latency_ms, ok, error }) => {
            if (!partial.toolCalls) return;
            for (let i = partial.toolCalls.length - 1; i >= 0; i--) {
              const tc = partial.toolCalls[i];
              if (tc.name === name && tc.status === 'running') {
                tc.status = ok ? 'ok' : 'error';
                tc.latencyMs = latency_ms;
                tc.error = error;
                break;
              }
            }
          },
          onToken: ({ text }) => {
            if (!text) return;
            partial.message = (partial.message || '') + text;
            options?.onTokenAppend?.(partial.message);
          },
          // 백엔드 groundedness 정정 재생성 — 지금까지 스트리밍된 본문 전체를 정정본으로 교체.
          onReplace: ({ text }) => {
            if (!text) return;
            partial.message = text;
            options?.onTokenAppend?.(partial.message);
          },
          // Lightweight 진행 indicator — chip 시스템과 무관하게 항상 표시.
          // kind='thinking' 또는 kind='tool'+name. BotMessage 가 partial 풍선 위에 한 줄로 렌더.
          onStatus: ({ kind, name }) => {
            partial.statusKind = kind;
            partial.statusToolName = name;
          },
          // 백엔드가 사용한 conversationId 통보 — activeId 갱신 + 목록 refetch
          onConversation: ({ conversationId: respConvId, isNew }) => {
            // 이 요청이 더는 활성 스트림이 아니거나(교체/취소), 사용자가 요청 시작 시점의
            // 대화에서 이미 다른 대화로 전환했다면 늦은 통보로 active 를 되돌리지 않는다 (재리뷰 P1)
            if (streamAbortController !== requestController) return;
            if (
              convStore.activeId !== (conversationId ?? null) &&
              convStore.activeId !== respConvId
            ) {
              return;
            }
            convStore.setActiveId(respConvId);
            if (isNew) {
              // 새 대화 — 목록에 추가됐을 가능성. refetch
              convStore.fetchList(userIdStr).catch(() => undefined);
            }
          },
          onDone: (data) => {
            partial.isPartial = false;
            // 표시 라벨이 '답변 받은 시각' 이므로 완료(스트림 종료) 시점으로 재기록 —
            // addMessage 의 시작(placeholder) 시각은 긴 스트림에서 실제 완료시각과 어긋남.
            partial.createdAt = new Date().toISOString();
            // status 도 같이 해제 — done 후 풍선 위에 indicator 잔존 방지.
            partial.statusKind = undefined;
            partial.statusToolName = undefined;
            // 응답 메타 (모델/토큰/소요시간) — 히스토리 GET 의 metaJson 과 동일 포맷으로 보관.
            if (data?.meta) {
              try {
                partial.metaJson = JSON.stringify(data.meta);
              } catch {
                partial.metaJson = undefined;
              }
            }
            // 인라인 차트 — 백엔드가 추이/Top-N 도구 결과에서 자동 첨부 (chartsJson 포맷 통일).
            if (data?.charts?.length) {
              try {
                partial.chartsJson = JSON.stringify(data.charts);
              } catch {
                partial.chartsJson = undefined;
              }
            }
            // 알람 임계치 추천 — recommend_alarm_thresholds 결과를 구조화 표(+ '알람 추가' 버튼)로.
            if (data?.alarmRecommendations?.length) {
              try {
                partial.alarmRecsJson = JSON.stringify(data.alarmRecommendations);
              } catch {
                partial.alarmRecsJson = undefined;
              }
            }
            // 구조화 응답 카드 — present_structured_answer 결과(단일 객체)를 고정 레이아웃으로 렌더.
            if (data?.structuredAnswer) {
              try {
                partial.structuredAnswerJson = JSON.stringify(data.structuredAnswer);
              } catch {
                partial.structuredAnswerJson = undefined;
              }
            }
            options?.onStreamDone?.(partial.message || '', true);
            // Backend 가 본문 botMsg append 후 generate_report 등의 후속 ChatMessage 를 추가했을 수 있음
            // (resultType="report"). botCreatedAt 을 lastTime 으로 GET → 본문 이후 메시지만 chatMessages 에 합류.
            // 이 호출이 없으면 사용자가 새로고침할 때까지 카드가 안 보임.
            const lastTime = data?.botCreatedAt;
            if (lastTime) {
              getChatMessage(lastTime).catch((e) =>
                console.warn('[chatbot] post-done fetch failed', e),
              );
            }
          },
          // payload 의 detail.type 은 streamChatbot 시그니처 (utils/streamChatbot.ts) 가
          // `string` (wide) 로 선언하므로 여기서 NavigateDetailInfo (narrow union) 로 좁혀
          // 받을 수 없다 (TS function parameter contravariance). wide 그대로 받고 body 안에서
          // ALLOWED_DETAIL_TYPES 로 runtime validate 후 NavigateDetailInfo 로 cast.
          onNavigate: async (payload: {
            path: string;
            label?: string;
            domain?: string;
            detail?: {
              type: string;
              target_id: string;
              target_name?: string;
              server?: string;
            };
            prefill?: {
              instance_id?: string;
              instance_name?: string;
              from_time?: string;
              to_time?: string;
              auto_search?: boolean;
              intent?: string;
              sub_kind?: string;
            };
          }) => {
            // 챗봇이 navigate_to_screen / open_target_detail / analyze_in_screen 도구로 path 를
            // 매칭한 즉시 좌측 main 영역(router-view)을 push.
            // open_target_detail → detail block 도착 → 라우팅 후 slide-detail 자동 오픈.
            // analyze_in_screen → prefill block 도착 → window.__chatbotScreenPrefill 등록 →
            //                     화면 onMount 가 consume 후 instance/time 자동 적용 + 검색.
            const { path, detail, prefill } = payload;
            // prefill 은 라우팅 직전에 등록해야 화면 onMounted 시 이미 존재.
            if (prefill && path) {
              registerChatbotScreenPrefill({ path, ...prefill });
            }
            try {
              if (path && router.currentRoute.value.fullPath !== path) {
                await router.push(path).catch(() => {
                  /* navigation duplicate / interrupted — 무시 */
                });
              }
            } catch (e) {
              console.warn('[chatbot] navigate failed', e);
              return;
            }
            if (!detail) return;
            // detail.type 은 stream payload 상 wide string. NavigateDetailInfo 의 좁은 union 에
            // 들어가는 값인지 검증 후 cast. 모르는 type 이면 라우팅까지만 했으니 silent skip.
            if (!(ALLOWED_DETAIL_TYPES as readonly string[]).includes(detail.type)) {
              console.warn('[chatbot] unknown detail.type, skip slide-detail', detail);
              return;
            }
            // 라우터 push 후 대상 화면이 mount 되어 slide-detail 호출에 필요한 store/permission 이
            // 준비될 시간을 잠깐 준다.
            await new Promise((resolve) => setTimeout(resolve, 250));
            try {
              await openSlideDetailFromChatbot(detail as NavigateDetailInfo);
            } catch (e) {
              console.warn('[chatbot] open_target_detail dispatch failed', e);
            }
          },
          onError: ({ message }) => {
            partial.message = `${partial.message || ''}\n\n${i18n.global.t('WORD.CHATBOT.MESSAGE.ERROR_PREFIX')} ${message}`;
            partial.isPartial = false;
            partial.statusKind = undefined;
            partial.statusToolName = undefined;
            options?.onStreamDone?.(partial.message || '', false);
          },
        },
        requestController.signal,
      );
      // 서버/프록시가 done·error 프레임 없이 스트림을 정상 EOF 로 닫으면 어떤 핸들러도
      // isPartial 을 못 끈다 — 메시지가 영구 typing 상태로 남지 않게 여기서 마감 (리뷰 P2)
      if (partial.isPartial) {
        partial.isPartial = false;
        partial.statusKind = undefined;
        partial.statusToolName = undefined;
        options?.onStreamDone?.(partial.message || '', false);
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      if (!/abort/i.test(msg)) {
        partial.message = `${partial.message || ''}\n\n${i18n.global.t('WORD.CHATBOT.MESSAGE.CONN_ERROR_PREFIX')} ${msg}`;
      }
      partial.isPartial = false;
      partial.statusKind = undefined;
      partial.statusToolName = undefined;
      options?.onStreamDone?.(partial.message || '', false);
    } finally {
      // 이 요청이 여전히 활성 스트림일 때만 공유 상태를 정리 — 교체된 경우(B 진행 중)
      // A의 finally 가 B의 controller/partial/로딩을 지우면 안 된다.
      if (streamAbortController === requestController) {
        streamAbortController = null;
        partialBotMessage.value = null;
        stopLoading();
      } else if (partialBotMessage.value === partial) {
        partialBotMessage.value = null;
      }
    }
  };

  const cancelStreaming = () => {
    if (streamAbortController) {
      streamAbortController.abort();
      streamAbortController = null;
      // controller 를 비우면 원 요청의 finally 소유권 검사가 스킵됨 — 로딩/partial 은 여기서 정리
      if (partialBotMessage.value) {
        const partial = partialBotMessage.value;
        // 중지 마커를 화면에도 즉시 남긴다 — 백엔드도 abort 턴을 같은 문구로 저장하므로
        // 이력 재조회 시 같은 모양이 된다 (중지 시점까지의 부분 답변은 그대로 보존).
        // 결합 규칙은 백엔드 ChatbotService 의 abort 분기와 **문구뿐 아니라 구분자까지** 같아야
        // 한다 — `부분답변 + "\n\n" + 마커`, 부분답변이 없으면 마커만. 한쪽만 바뀌면 같은 중지인데
        // 스트리밍 화면과 이력 본문이 미세하게 달라진다.
        partial.message = partial.message
          ? `${partial.message}\n\n${STOPPED_BY_USER_TEXT}`
          : STOPPED_BY_USER_TEXT;
        // 실행 중이던 도구는 결과(tool_result)를 영영 못 받는다. 남겨두면 BotMessage 의 tool-chip 이
        // isPartial 과 무관하게 running 스피너를 계속 돌려, 종료된 메시지 위에서 혼자 로딩 중이 된다.
        partial.toolCalls?.forEach((tc) => {
          if (tc.status === 'running') tc.status = 'error';
        });
        partial.statusKind = undefined;
        partial.statusToolName = undefined;
        partial.isPartial = false;
        partialBotMessage.value = null;
      }
      stopLoading();
    }
  };

  /** 차트 raw 데이터를 한 메시지에 같이 보내되 사용자 표시는 짧게 유지하기 위한 size cap. */
  const CHART_DATA_INLINE_LIMIT = 8000;

  /**
   * BotMessage 가 markdown 본문의 `target://kind/sub/<id>` anchor 클릭을 가로채 호출.
   * detail.type 별 화면으로 라우팅하고 slide-detail 도 자동으로 연다 (open_target_detail 과 동일).
   */
  const KIND_PATHS: Record<string, string> = {
    host: '/infrastructure/host',
    container_host: '/infrastructure/host',
    application: '/application/was',
    was: '/application/was',
    webserver: '/application/webServer',
    web_server: '/application/webServer',
    database: '/database/instance',
    pod: '/kubernetes/pod',
    node: '/kubernetes/node',
    namespace: '/kubernetes/namespace',
    container: '/kubernetes/container',
    network_device: '/infrastructure/networkDevice',
  };
  const KIND_DETAIL_TYPE: Record<string, NavigateDetailInfo['type']> = {
    host: 'host',
    container_host: 'host',
    application: 'was',
    was: 'was',
    webserver: 'web_server',
    web_server: 'web_server',
    database: 'instance',
    pod: 'pod',
    node: 'node',
    namespace: 'namespace',
    container: 'container',
    network_device: 'network_device',
  };

  const dispatchTargetDetailFromBot = async (raw: {
    type: string;
    target_id: string;
    target_name?: string;
    sub_kind?: string;
    /** 선택적 tab hint — markdown link `?tab=` 또는 backend navigate event 의 tab 필드. */
    tab?: string;
  }) => {
    const kindKey = (raw.type || '').toLowerCase();
    const path = KIND_PATHS[kindKey];
    const detailType = KIND_DETAIL_TYPE[kindKey];
    if (!path || !detailType) {
      console.warn('[chatbot] dispatchTargetDetailFromBot: unknown kind', raw.type);
      return;
    }
    try {
      if (router.currentRoute.value.fullPath !== path) {
        await router.push(path).catch(() => {
          /* duplicate / interrupted — 무시 */
        });
        // 라우트 mount 대기
        await new Promise((r) => setTimeout(r, 250));
      }
      await openSlideDetailFromChatbot({
        type: detailType,
        target_id: raw.target_id,
        target_name: raw.target_name,
        sub_kind: raw.sub_kind,
        // host kind 의 server hint 는 sub_kind 가 없으면 'host' 로 default (container_host 인 경우는 주입된 값으로)
        server: kindKey === 'container_host' ? 'container_host' : 'host',
        tab: raw.tab,
      });
    } catch (e) {
      console.warn('[chatbot] dispatchTargetDetailFromBot failed', e);
    }
  };

  /**
   * 대시보드 위젯 차트 분석. AnalysisButton 클릭 시 호출.
   *  - 사용자 채팅 표시: "{title} 차트 분석해주세요" (간결) + 차트 썸네일
   *  - 백엔드 전송: 위 텍스트 + raw chart JSON 을 ```json ... ``` 블록으로 inline →
   *    Gemini 가 메트릭 추세·이상치·임계 초과 패턴을 분석해 답변.
   *  - balloon 콜백을 통해 같은 stream 결과가 차트 옆 풍선에도 동기화 표시되도록 함.
   *
   *  history 에 raw JSON 까지 모두 누적되면 컨텍스트가 빠르게 차므로 size 를 cap 한다.
   */
  const analyzeChart = async (
    title: string,
    chartData: unknown,
    extra?: {
      imageUrl?: string;
      sourcePath?: string;
      onTokenAppend?: (text: string) => void;
      onStreamDone?: (text: string, ok: boolean) => void;
    },
  ) => {
    openChat();
    const safeTitle = title?.trim() || i18n.global.t('WORD.CHART');
    const display = i18n.global.t('WORD.CHATBOT.MESSAGE.CHART_ANALYZE_REQUEST', {
      title: safeTitle,
    });
    let json = '';
    try {
      json = JSON.stringify(chartData ?? {});
    } catch {
      json = '';
    }
    if (json.length > CHART_DATA_INLINE_LIMIT) {
      json = `${json.substring(0, CHART_DATA_INLINE_LIMIT)}\n…(truncated, total ${json.length} chars)`;
    }
    // 사용자/history 에는 깔끔한 'display' 만 남기고, raw JSON 은 hiddenContext 로 backend 에만 전달.
    // backend 는 마지막 user turn 에 hiddenContext 를 임시로 append 하지만 history.append 에는
    // 'display' 만 저장 → 페이지 reload·history fetch 시 화면에 raw JSON 이 안 보인다.
    const hiddenContext = json
      ? `다음은 분석 대상 차트(${safeTitle})의 raw 데이터(JSON)입니다. 메트릭 추세, 이상치(spike/dip), 임계 초과/이상 패턴을 식별하고 한국어 bullet 으로 요약해주세요. 답변은 markdown 헤더/리스트/볼드를 적극 활용해 가독성 높게.\n❗ 이 답변은 차트 분석 1회성 응답이므로 답변 끝에 [SUGGESTIONS] 블록 출력 금지.\n\n\`\`\`json\n${json}\n\`\`\``
      : `차트 ${safeTitle} 의 raw 데이터가 제공되지 않았습니다. 화면에서 보이는 정보 기반으로 분석해주세요.\n❗ 답변 끝에 [SUGGESTIONS] 블록 출력 금지 (차트 분석 1회성 응답).`;
    await sendStreamingMessage(display, {
      imageUrl: extra?.imageUrl,
      sourcePath: extra?.sourcePath,
      sourceTitle: safeTitle,
      hiddenContext,
      onTokenAppend: extra?.onTokenAppend,
      onStreamDone: extra?.onStreamDone,
    });
  };

  /**
   * 알람 임계치 추천 표의 '알람 추가' — 추천값을 prefill 로 등록하고 사용자 알람 규칙 생성
   * 화면(/alert/userAlert)으로 이동. 화면이 prefill 을 소비해 생성 창을 추천값으로 연다.
   * 실제 생성(저장)은 사용자가 화면에서 수행 (대상 인스턴스도 화면에서 선택).
   */
  const ALARM_PREFILL_PATH = '/alert/userAlert';
  const applyAlarmRecommendation = async (payload: {
    rec: AlarmRecommendation;
    metric: AlarmRecommendMetric;
  }) => {
    const { rec, metric } = payload;
    if (!metric?.recommend) return;
    registerChatbotScreenPrefill({
      path: ALARM_PREFILL_PATH,
      alarm: {
        category: metric.apply.category,
        targetCategory: metric.apply.targetCategory || metric.apply.category,
        dataId: metric.apply.dataId,
        metricName: metric.name,
        targetId: metric.apply.targetId,
        targetName: metric.apply.targetName || rec.instance_name || '',
        warning: metric.recommend.warning,
        critical: metric.recommend.critical,
        warningComparison: metric.recommend.warningComparison,
        criticalComparison: metric.recommend.criticalComparison,
      },
    });
    try {
      if (router.currentRoute.value.path !== ALARM_PREFILL_PATH) {
        await router.push(ALARM_PREFILL_PATH).catch(() => undefined);
      }
      // 같은 화면 재클릭(이미 mounted) 케이스까지 커버 — 화면이 듣는 이벤트 발사.
      window.dispatchEvent(new CustomEvent('chatbot:alarm-prefill'));
    } catch (e) {
      console.warn('[chatbot] applyAlarmRecommendation route failed', e);
    }
  };

  /**
   * 대화 새로 시작 — 이전 대화는 보존하고 새 대화로 active 전환.
   * 헤더의 "초기화" 버튼 또는 "새 대화" 버튼이 호출.
   */
  const resetConversation = async () => {
    cancelStreaming();
    partialBotMessage.value = null;
    const userIdStr = String(userInfo.value?.userId ?? -1);
    const convStore = useChatConversationsStore();
    try {
      const res = await customAxiosInstance<{
        status: string;
        conversationId: string;
        isNew: boolean;
      }>({
        url: '/api/v9/chatbot/messages',
        method: 'DELETE',
        params: { userId: userIdStr },
      });
      const newId = res.data?.conversationId;
      if (newId) convStore.setActiveId(newId);
      // 목록 refetch (새 대화 추가됐을 가능성)
      convStore.fetchList(userIdStr).catch(() => undefined);
    } catch (e) {
      // backend 실패해도 front 는 비워준다 (다음 GET 으로 동기화).
      console.warn('[chatbot] reset conversation request failed', e);
    } finally {
      clearMessages();
      stopLoading();
    }
  };

  const handleResize = () => {
    const stored = getDefaultChatPosition();
    chatPosition.value = adjustPositionToScreen(stored);
  };

  return {
    openChat,
    closeChat,
    toggleChat,
    addMessage,
    clearMessages,
    getChatMessage,
    setChatPosition,
    startLoading,
    stopLoading,
    handleResize,
    isChatWindowOpen,
    chatPosition,
    chatMessages,
    isLoading,
    sendStreamingMessage,
    analyzeChart,
    dispatchTargetDetailFromBot,
    applyAlarmRecommendation,
    cancelStreaming,
    resetConversation,
    partialBotMessage,
    dockMode,
    dockWidth,
    setDockMode,
    toggleDockMode,
    setDockWidth,
    // 타겟 선택 (feature: chatbot-target-conversation)
    selectedTargets,
    pinnedTargetIds,
    recentTargetIds,
    isTargetSelected,
    addSelectedTarget,
    removeSelectedTarget,
    toggleSelectTarget,
    clearSelectedTargets,
    isTargetPinned,
    togglePinTarget,
    pushRecentTargets,
    loadPersistedTargets,
    MAX_SELECTED_TARGETS,
  };
});
